import { useState } from 'react';
import { get } from 'lodash-es';

import { analytics } from '@/analytics';
import {
    type ExportColumnDefinition,
    getExportColumnHeader,
    getExportColumnPath,
} from '@/components/molecules/ExportDataDropDown/DownloadOptions';
import { useCageOptional } from '@/contexts/cage';
import { uniqueExcelSheetNames } from '@/utils/excelSheetName';
import { sanitizeFileName } from '@/utils/fileNameFormatting';
import { saveBlob } from '@/utils/saveBlob';
import { type XlsxSheet, xlsxBlob } from '@/utils/xlsxWorkbook';

export interface ExtraExportSheet {
    name: string;
    header: string[];
    rows: (string | number | null)[][];
}

/** Primary `#header` block, then an optional blank line + titled extra block. */
export function buildCsvContent(
    header: string[],
    rows: (string | number | null)[][],
    extraSheet?: ExtraExportSheet
): string {
    const lines = [`#${header.join(',')}`, ...rows.map((row) => row.join(','))];
    if (extraSheet) {
        lines.push(
            '',
            extraSheet.name,
            extraSheet.header.join(','),
            ...extraSheet.rows.map((row) => row.join(','))
        );
    }
    return lines.join('\n');
}

function SaveCSV(
    filename: string,
    header: string[],
    rows: (string | number | null)[][],
    extension: string,
    extraSheet?: ExtraExportSheet
) {
    const blob = new Blob([buildCsvContent(header, rows, extraSheet)], {
        type: 'data:text/csv;charset=utf-8',
    });

    saveBlob(blob, `${filename}.${extension}`);
}

async function SaveXLSX(
    filename: string,
    header: string[],
    rows: (string | number | null)[][],
    sheetName = 'data',
    extraSheet?: ExtraExportSheet
) {
    const [primaryName, extraName] = extraSheet
        ? uniqueExcelSheetNames([sheetName, extraSheet.name])
        : uniqueExcelSheetNames([sheetName]);
    const sheets: XlsxSheet[] = [{ name: primaryName, rows: [header, ...rows] }];
    if (extraSheet && extraName) {
        sheets.push({ name: extraName, rows: [extraSheet.header, ...extraSheet.rows] });
    }
    saveBlob(await xlsxBlob(sheets), `${filename}.xlsx`);
}

interface DownloadCSVXLSXProps {
    data: unknown[];
    nameToInclude?: string;
    COLUMNS?: ExportColumnDefinition[];
    sheetName?: string;
    extraSheet?: ExtraExportSheet;
}
export const useDownloadCSVXLSX = ({
    data,
    nameToInclude,
    COLUMNS = [],
    sheetName = 'data',
    extraSheet,
}: DownloadCSVXLSXProps) => {
    // Optional on purpose: this hook also powers the download button on region and location pages,
    // which render above the CageProvider. There it just contributes no pen/location name.
    const cageContext = useCageOptional();
    const [loadingCSV, setLoadingCSV] = useState(false);
    const [loadingXLSX, setLoadingXLSX] = useState(false);
    const [error, setError] = useState(false);
    const joinedFilename = [
        cageContext?.location?.name ?? '',
        cageContext?.cage?.name ?? '',
        nameToInclude ? nameToInclude : '',
    ]
        .filter((x) => x)
        .join('_');
    const filename = sanitizeFileName(joinedFilename);
    const header = COLUMNS.map((item) => getExportColumnHeader(item));
    const rows =
        data?.map((day) => {
            // Keys mirror the export column paths callers pass in ('location.name', 'cage.name').
            //
            // Built field by field on purpose. Several screens also declare
            // 'location.timezone' and 'location.sWeightPct' columns, which have always resolved to
            // null here because the old context location shape carried neither field. The new
            // blocks would make `timezone` resolvable while `sWeightPct` — now under `stock` —
            // stayed null, so spreading the block through would half-fix them. Both are left
            // unresolved deliberately; whether those columns should carry data or be dropped is a
            // separate decision.
            const rowSource = {
                location: { name: cageContext?.location?.name },
                cage: { name: cageContext?.cage?.name ?? '' },
                day,
            };
            return COLUMNS.map((item) => {
                const col = getExportColumnPath(item);
                return get(rowSource, col) ?? null;
            });
        }) ?? [];
    const downloadCSV = () => {
        const extension = 'csv';
        setLoadingCSV(true);
        analytics.exportTriggered({
            exportType: nameToInclude ?? 'data_export',
            fileFormat: 'csv',
        });
        Promise.resolve(SaveCSV(filename, header, rows, extension, extraSheet))
            .catch((err) => setError(err))
            .finally(() => setLoadingCSV(false));
    };

    const downloadXLSX = () => {
        setLoadingXLSX(true);
        analytics.exportTriggered({
            exportType: nameToInclude ?? 'data_export',
            fileFormat: 'xlsx',
        });
        Promise.resolve(SaveXLSX(filename, header, rows, sheetName, extraSheet))
            .catch((err) => setError(err))
            .finally(() => setLoadingXLSX(false));
    };

    return {
        loadingCSV,
        loadingXLSX,
        error,
        downloadCSV,
        downloadXLSX,
    };
};
